import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider
} from '@mui/material';
import { Close, Gavel } from '@mui/icons-material';

const TNCModal = ({ open, onClose }) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '85vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 3, 
        bgcolor: 'white',
        borderBottom: '1px solid #eee',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Gavel sx={{ color: '#2563eb', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111' }}>
              Terms and Conditions
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 0.25 }}>
              Last updated: January 13, 2026
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#999' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4, bgcolor: 'white' }}>
        
        {/* Introduction */}
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          By using CRYPTOLOGICS-AI.COM (the "Site") and/or registering for any of its Services, 
          including Cryptologics-AI Premium level Services, you agree to follow and be bound by 
          the following terms of use ("Terms of Use") and you agree to comply with all applicable 
          laws and regulations. In these Terms of Use, the words "you" and "your" refer to each 
          registered customer or Site visitor, "we", "us" and "our" refer to CRYPTOLOGICS-AI LLC, 
          a Texas Limited Liability Company.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Description of Services */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          Description of Services
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          Cryptologics-ai is a personal cryptocurrency portfolio automation service that includes 
          tools for asset allocation, automatic trading signal generation, and backtesting. This 
          service is not intended for trading cryptocurrencies, cryptographic tokens or other 
          digital rights or assets (collectively "Tokens"), and Cryptologics-ai is not a Token 
          exchange. Rather, via the Cryptologics-ai portal, you access your own accounts on the Token exchanges.
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          Some features of the Cryptologics-ai portal are provided to you at no cost. This prediction 
          of percentage change in price is based on historical data of the Tokens which are analyzed 
          using analytical tools and libraries and are not guaranteed to be accurate and correct.
        </Typography>

        {/* Risk Warning */}
        <Box sx={{ 
          bgcolor: '#fef2f2', 
          border: '1px solid #fecaca',
          borderRadius: 2, 
          p: 2.5, 
          mb: 3 
        }}>
          <Typography sx={{ fontWeight: 600, color: '#dc2626', fontSize: '0.95rem', lineHeight: 1.7 }}>
            ⚠️ THE RISK OF LOSS IN TRADING VIRTUAL CURRENCY OR CRYPTOCURRENCY CAN BE SUBSTANTIAL. 
            YOU SHOULD CAREFULLY CONSIDER WHETHER SUCH TRADING IS SUITABLE FOR YOU IN LIGHT OF 
            YOUR FINANCIAL CONDITION.
          </Typography>
        </Box>

        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          Cryptologics-ai LLC provides Services that directly make trades on behalf of the User. 
          The information contained on the Site and Services is not financial or commodity trading 
          advice and is not guaranteed to be correct, complete or up-to-date.
        </Typography>

        {/* Age Requirement */}
        <Box sx={{ 
          bgcolor: '#eff6ff', 
          border: '1px solid #bfdbfe',
          borderRadius: 2, 
          p: 2.5, 
          mb: 3 
        }}>
          <Typography sx={{ fontWeight: 500, color: '#1d4ed8', fontSize: '0.95rem' }}>
            YOU AGREE THAT BY USING THE SITE AND THE SERVICES YOU ARE AT LEAST 18 YEARS OF AGE 
            AND LEGALLY ABLE TO ENTER INTO A CONTRACT.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* License to Use */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          1. License to Use
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          Cryptologics-ai LLC grants you a limited, personal, non-exclusive, non-transferable 
          license to use our Site and its Services for your own personal use. You acknowledge 
          and agree that you have no right to modify, edit, copy, reproduce, create derivative 
          works of, alter, enhance or in any way exploit the Site without express written consent.
        </Typography>

        {/* Ownership */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          2. Ownership
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          This Site is owned and operated by Cryptologics-ai LLC, a Texas Limited Liability Company. 
          All right, title and interest in and to information, documents, logos, graphics, images 
          and materials provided on this Site are owned by Cryptologics-ai LLC.
        </Typography>

        {/* Investment Advice */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          3. Investment Advice Disclaimer
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 1 }}>
          Cryptologics-ai LLC is not a commodity or financial investment advisor. The Site or Services do not:
        </Typography>
        <Box component="ul" sx={{ color: '#444', lineHeight: 2, pl: 3, mb: 3 }}>
          <li>Obtain access or direct client accounts or funds in any form</li>
          <li>Provide information or trading advice tailored to particular clients</li>
          <li>Provide trading advice regarding Bitcoin, Ethereum, or any other cryptocurrency based upon future market conditions</li>
        </Box>

        {/* Risk Factors */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          4. Risk Factors
        </Typography>
        <Typography sx={{ fontWeight: 600, color: '#222', mb: 0.5 }}>Price Volatility</Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          The price of a virtual currency is based on perceived value and subject to changes in 
          sentiment, making these products highly volatile.
        </Typography>
        <Typography sx={{ fontWeight: 600, color: '#222', mb: 0.5 }}>Valuation and Liquidity</Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          Virtual currencies can be traded through various exchanges worldwide. The lack of a 
          centralized pricing source poses valuation challenges.
        </Typography>
        <Typography sx={{ fontWeight: 600, color: '#222', mb: 0.5 }}>Cybersecurity</Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          Cybersecurity risks include hacking vulnerabilities. A cybersecurity event could result 
          in substantial, immediate and irreversible loss.
        </Typography>
        <Typography sx={{ fontWeight: 600, color: '#222', mb: 0.5 }}>Regulatory Landscape</Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          Virtual currencies face an uncertain regulatory landscape which may impact prices and acceptance.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Risk Disclaimer */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          5. Risk Disclaimer
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          Cryptologics-ai LLC shall not be liable for loss or damage as a result of reliance on 
          the information contained within this website including data, quotes, charts and buy/sell signals.
        </Typography>
        <Typography sx={{ fontWeight: 600, color: '#dc2626', mb: 3 }}>
          DO NOT EVER INVEST MORE THAN YOU CAN AFFORD TO LOSE.
        </Typography>

        {/* Earnings Disclaimer */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          6. Earnings Disclaimer
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          Cryptologics-ai LLC makes absolutely no guarantee that by following the information or 
          advice available from the Site or Services that you will make money or generate profits. 
          Results depend on marketplace conditions, individual experience, and factors beyond your control.
        </Typography>

        {/* Privacy Policy */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          7. Privacy Policy
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          Cryptologics-ai LLC respects your privacy. Our Privacy Policy is incorporated into this 
          Agreement by reference. You are responsible for maintaining the confidentiality of your 
          password and must notify us immediately of any unauthorized use of your account.
        </Typography>

        {/* No Warranty */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          8. No Warranty
        </Typography>
        <Box sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 3 }}>
          <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.7, color: '#4b5563' }}>
            THE SITE AND ALL MATERIALS ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. 
            CRYPTOLOGICS-AI LLC EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS 
            OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
            AND NON-INFRINGEMENT.
          </Typography>
        </Box>

        {/* Limitation of Liability */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          9. Limitation of Liability
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          You will hold Cryptologics-ai LLC and its officers, directors, employees, and agents 
          harmless for any indirect, punitive, special, incidental, or consequential damage. 
          Liability is limited to the amount paid for the products and/or services.
        </Typography>

        {/* Dispute Resolution */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          10. Dispute Resolution
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          Most concerns can be resolved by contacting Cryptologics-ai LLC at 16635 Spring Cypress 
          #535 Cypress, TX USA 77410. Unresolved disputes will be settled through binding arbitration. 
          By entering into these Terms, you waive the right to a trial by jury or to participate in a class action.
        </Typography>

        {/* Governing Law */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          11. Governing Law
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          Your rights and obligations shall be governed by the laws of the State of Texas, 
          excluding its choice of law rules.
        </Typography>

        {/* Account Registration */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          12. Account Registration
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          To utilize the Site's Services, you must register for an account with a username and 
          password. You are free to cancel your subscription at any time by accessing your account dashboard.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Acknowledgement */}
        <Box sx={{ 
          bgcolor: '#f0fdf4', 
          border: '1px solid #bbf7d0',
          borderRadius: 2, 
          p: 2.5
        }}>
          <Typography sx={{ fontWeight: 500, color: '#166534', fontSize: '0.95rem', lineHeight: 1.7 }}>
            BY USING CRYPTOLOGICS-AI LLC'S SERVICES OR ACCESSING THE CRYPTOLOGICS-AI.COM SITE, 
            YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF USE AND AGREE TO BE BOUND BY THEM.
          </Typography>
        </Box>

      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #eee' }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            bgcolor: '#2563eb',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
            '&:hover': {
              bgcolor: '#1d4ed8'
            }
          }}
        >
          I Understand & Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TNCModal;